import { createContext, useContext, useState, useEffect, useCallback, useRef, Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@rnd-payplus/alloy-react';

// ─── Types ───────────────────────────────────────────────────────────────────

interface PageHeaderConfig {
  title: string;
  badge?: ReactNode;
  onBack?: () => void;
  primaryAction?: ReactNode;
  secondaryActions?: ReactNode;
}

interface BreadcrumbEntry {
  label: string;
  href?: string;
}

interface PageShellContextValue {
  pageHeader: PageHeaderConfig | null;
  breadcrumbs: ReactNode | null;
  alert: ReactNode | null;
  setPageHeader: (config: PageHeaderConfig | null) => void;
  setBreadcrumbs: (crumbs: ReactNode | null) => void;
  setAlert: (alert: ReactNode | null) => void;
}

// ─── Context ─────────────────────────────────────────────────────────────────

export const PageShellContext = createContext<PageShellContextValue | null>(null);

export function PageShellProvider({ children }: { children: ReactNode }) {
  const [pageHeader, setPageHeader] = useState<PageHeaderConfig | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<ReactNode | null>(null);
  const [alert, setAlert] = useState<ReactNode | null>(null);

  return (
    <PageShellContext.Provider
      value={{ pageHeader, breadcrumbs, alert, setPageHeader, setBreadcrumbs, setAlert }}
    >
      {children}
    </PageShellContext.Provider>
  );
}

function usePageShellContext() {
  const ctx = useContext(PageShellContext);
  if (!ctx) throw new Error('usePageShell must be used within PageShellProvider');
  return ctx;
}

// ─── Helper: build breadcrumb ReactNode from entries ─────────────────────────

function buildBreadcrumbs(entries: BreadcrumbEntry[]): ReactNode {
  return (
    <Breadcrumb>
      <BreadcrumbList>
        {entries.map((entry, i) => {
          const isLast = i === entries.length - 1;
          return isLast ? (
            <BreadcrumbItem key={entry.label}>
              <BreadcrumbPage>{entry.label}</BreadcrumbPage>
            </BreadcrumbItem>
          ) : (
            <Fragment key={entry.label}>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={entry.href ?? '/dashboard'}>{entry.label}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function breadcrumbKey(entries: BreadcrumbEntry[]): string {
  return entries.map(e => `${e.label}:${e.href ?? ''}`).join('|');
}

// ─── Hook ────────────────────────────────────────────────────────────────────

interface UsePageShellConfig {
  title: string;
  badge?: ReactNode;
  onBack?: () => void;
  primaryAction?: ReactNode;
  secondaryActions?: ReactNode;
  breadcrumbs?: BreadcrumbEntry[];
  updateKey?: string;
}

export function usePageShell(config: UsePageShellConfig) {
  const { setPageHeader, setBreadcrumbs, setAlert } = usePageShellContext();

  // Store the full config in a ref so the effect always reads the latest
  // without needing ReactNode values as dependencies (they are new objects
  // every render and would cause infinite update loops).
  const configRef = useRef(config);
  configRef.current = config;

  const crumbKey = config.breadcrumbs ? breadcrumbKey(config.breadcrumbs) : '';

  // Only re-run when the title or breadcrumbs structurally change.
  // Actions are read from the ref so they're always current.
  useEffect(() => {
    const c = configRef.current;
    setPageHeader({
      title: c.title,
      badge: c.badge,
      onBack: c.onBack,
      primaryAction: c.primaryAction,
      secondaryActions: c.secondaryActions,
    });

    if (c.breadcrumbs) {
      setBreadcrumbs(buildBreadcrumbs(c.breadcrumbs));
    }

    return () => {
      setPageHeader(null);
      setBreadcrumbs(null);
      setAlert(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.title, crumbKey, config.updateKey]);

  const setPageAlert = useCallback(
    (alert: ReactNode | null) => setAlert(alert),
    [setAlert],
  );

  return { setAlert: setPageAlert };
}
